"use client";

import React from "react";
import { ArrowRight, Activity, Beaker, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
interface SolutionItem {
  id: string;
  title: string;
  category: string;
  description: string;
  href: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  icon: "activity" | "beaker";
  keyBenefits: string[];
}
const solutions: SolutionItem[] = [{
  id: "pat",
  title: "Process Analytical Technology",
  category: "Real-Time Monitoring",
  description: "In-line and on-line analytical instrumentation designed for continuous process monitoring, endpoint detection, and robust quality control in regulated manufacturing environments.",
  href: "/ProcessAnalyticalTechnologyPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_analytical_technology.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_analytical_technology.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_analytical_technology.webp-wvc-sizes",
  icon: "activity",
  keyBenefits: ["Real-time blend uniformity & reaction tracking", "Non-destructive Raman & NIR optical probes", "Direct integration with plant control systems"]
}, {
  id: "chem-mfg",
  title: "Chemical Manufacturing",
  category: "Industrial & API Synthesis",
  description: "Specialized analytical tools, automated sampling, and solvent verification workflows that streamline chemical synthesis, active ingredient testing, and hazardous area compliance.",
  href: "/ChemicalManufacturingPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/chemical_manufacturing_suite.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/chemical_manufacturing_suite.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/chemical_manufacturing_suite.png-wvc-sizes",
  icon: "beaker",
  keyBenefits: ["Hazardous area ATEX/Ex certified options", "Solvent purity & raw material qualification", "Consistent batch-to-batch yield optimization"]
}];
export default function RelatedSolutions() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="relatedsolutions" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }} variants={containerVariants} className="space-y-12">
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-border pb-8">
            <div className="max-w-2xl space-y-3">
              <Badge variant="outline" className="text-primary border-primary/30">
                Explore Capabilities
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                Related Industrial Solutions
              </h2>
              <p className="text-muted-foreground text-base md:text-lg">
                Explore our specialized analytical systems, sensors, and turnkey validation workflows tailored for pharmaceutical and chemical production lines.
              </p>
            </div>
            <div>
              <Link to="/SolutionsPage">
                <Button variant="outline" className="group">
                  <span>View All Solutions</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Solutions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {solutions.map((solution, i) => {
            const IconComponent = solution.icon === "activity" ? Activity : Beaker;
            return <motion.div key={solution.id} data-index={i} variants={cardVariants} className="h-full">
                  <Card className="h-full flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] hover:border-primary/40 overflow-hidden group">
                    <div>
                      {/* Image Preview */}
                      <div className="relative aspect-video w-full overflow-hidden bg-muted">
                        <img src={solution.imageSrc} data-wvc-srcset={solution.imageSrcset} data-wvc-sizes={solution.imageSizes} alt={solution.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        <div className="absolute top-4 left-4">
                          <Badge variant="secondary" className="bg-background/90 text-foreground backdrop-blur-xs">
                            {solution.category}
                          </Badge>
                        </div>
                      </div>

                      <CardHeader className="pt-6">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="p-2 rounded-md bg-secondary text-foreground">
                            <IconComponent className="h-5 w-5 text-primary" />
                          </div>
                          <CardTitle className="text-xl md:text-2xl font-bold text-card-foreground">
                            {solution.title}
                          </CardTitle>
                        </div>
                        <CardDescription className="text-base text-muted-foreground leading-relaxed">
                          {solution.description}
                        </CardDescription>
                      </CardHeader>

                      <CardContent className="space-y-4">
                        <div className="space-y-2 border-t border-border/60 pt-4">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Key Application Highlights
                          </p>
                          <ul className="space-y-2 text-sm text-foreground">
                            {solution.keyBenefits.map((benefit, benefitIndex) => <li key={benefitIndex} data-index={benefitIndex} className="flex items-start gap-2.5">
                                <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                                <span>{benefit}</span>
                              </li>)}
                          </ul>
                        </div>
                      </CardContent>
                    </div>

                    <CardFooter className="pt-4 pb-6">
                      <Link to={solution.href} className="w-full">
                        <Button className="w-full justify-between group/btn">
                          <span>Explore {solution.title}</span>
                          <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                        </Button>
                      </Link>
                    </CardFooter>
                  </Card>
                </motion.div>;
          })}
          </div>
        </motion.div>
      </div>
    </section>;
}